import React from "react";
import { Box, Chip, Skeleton, Stack, Typography, alpha } from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import { describeRules } from "../utils/propertyRules";

/**
 * Compact badge for a property card — one chip, not five. Tells the tenant at a
 * glance how strict the exit terms are; the full read-out lives in the panel
 * below, which only opens on demand.
 */
export function RulesBadge({ rules, size = "small" }) {
  const info = describeRules(rules);

  if (!info) {
    return (
      <Chip
        size={size}
        icon={<GavelIcon sx={{ fontSize: 15 }} />}
        label="Terms not published"
        sx={{
          fontWeight: 600,
          fontSize: 11.5,
          color: "#64748b",
          bgcolor: alpha("#64748b", 0.1),
          "& .MuiChip-icon": { color: "#64748b" },
        }}
      />
    );
  }

  return (
    <Chip
      size={size}
      icon={<GavelIcon sx={{ fontSize: 15 }} />}
      label={
        info.noticePeriod
          ? `${info.label} terms • ${info.noticePeriod} notice`
          : `${info.label} terms`
      }
      sx={{
        fontWeight: 700,
        fontSize: 11.5,
        color: info.accent,
        bgcolor: alpha(info.accent, 0.12),
        "& .MuiChip-icon": { color: info.accent },
      }}
    />
  );
}

/**
 * Full plain-English read-out of the rule-set a landlord attached to the flat:
 * notice periods, exit penalties, what gets someone evicted on the spot, how
 * the deposit comes back. This is the single most consequential thing a tenant
 * agrees to and it was previously invisible until after the agreement existed.
 */
export default function PropertyRulesPanel({ rules, loading, accent }) {
  if (loading) {
    return (
      <Stack spacing={1.2}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rounded" height={54} />
        ))}
      </Stack>
    );
  }

  const info = describeRules(rules);

  if (!info) {
    return (
      <Box
        sx={{
          p: 2.2,
          borderRadius: "14px",
          border: "1px dashed #d7dce6",
          bgcolor: "#fafbfd",
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 0.4 }}>
          Terms not published yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This landlord hasn't attached a termination and violation rule-set to
          the property. Ask for the exit terms in writing before you commit.
        </Typography>
      </Box>
    );
  }

  const tone = accent || info.accent;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 1.8,
        }}
      >
        <Chip
          size="small"
          label={`${info.label} rule-set`}
          sx={{
            fontWeight: 700,
            color: "#fff",
            bgcolor: info.accent,
          }}
        />
        <Typography variant="body2" color="text.secondary">
          {info.tagline}
        </Typography>
      </Box>

      <Stack spacing={1.1}>
        {info.summary.map((row) => (
          <Box
            key={row.label}
            sx={{
              p: 1.6,
              borderRadius: "12px",
              border: "1px solid #eceff5",
              bgcolor: alpha(tone, 0.03),
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: 0.8,
                textTransform: "uppercase",
                color: tone,
                mb: 0.5,
              }}
            >
              {row.label}
            </Typography>
            <Typography variant="body2" sx={{ color: "#37425c" }}>
              {row.text}
            </Typography>
          </Box>
        ))}
      </Stack>

      {rules?.additionalRules ? (
        <Box
          sx={{
            mt: 1.6,
            p: 1.6,
            borderRadius: "12px",
            border: "1px solid #fde68a",
            bgcolor: "#fffbeb",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: 0.8,
              textTransform: "uppercase",
              color: "#b45309",
              mb: 0.5,
            }}
          >
            Additional rules
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {rules.additionalRules}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

// The rule-set rendered as report sections, so the PDF and the on-screen panel
// can never drift apart.
export function rulesReportSections(rules) {
  const info = describeRules(rules);

  if (!info) {
    return [
      {
        heading: "Termination & violation terms",
        type: "text",
        text: "",
        empty: "The landlord has not published a rule-set for this property.",
      },
    ];
  }

  const sections = [
    {
      heading: "Termination & violation terms",
      note: `${info.label} rule-set — ${info.tagline}`,
      type: "table",
      columns: ["Scenario", "What applies"],
      rows: info.summary.map((row) => [row.label, row.text]),
    },
  ];

  if (rules?.additionalRules) {
    sections.push({
      heading: "Additional rules",
      type: "text",
      text: rules.additionalRules,
    });
  }

  return sections;
}
